/**
 * The day axis of a conversation: which calendar day a message belongs to, what
 * to call that day, and how to cut a message list into days.
 *
 * The timeline used to print only `HH:MM`, which hides how much time passed
 * between two bubbles. In prod that is not a detail: lead XZ-418 is 58 messages
 * spread over 2 days 57 days apart, and nothing on screen said so.
 *
 * Deliberately separate from `time-ago.ts`: that one answers "how long ago"
 * relative to now, this one answers "on which day". Same reason `reply-window.ts`
 * keeps its own formatter — merging helpers whose rounding rules differ is how
 * they end up wrong for both callers.
 */

// `weekday day de month`, e.g. "lunes 25 de agosto". Same shape as
// formatVisitDateTime in components/visits/visit-status.ts, minus the clock.
const DAY_FORMAT = new Intl.DateTimeFormat("es-MX", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

const DAY_WITH_YEAR_FORMAT = new Intl.DateTimeFormat("es-MX", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

/**
 * Stable id for the LOCAL calendar day a timestamp falls on.
 *
 * Built from the local getters, never from the ISO string's `YYYY-MM-DD` prefix:
 * `createdAt` is UTC, so slicing it would move every message sent between local
 * midnight and the UTC offset onto the wrong day. That is not hypothetical —
 * the pilot's timeline has messages at 12:04 a.m.
 */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * What the divider above a day's messages reads: "Hoy", "Ayer", or the date.
 *
 * The year is appended only when it differs from the reference year — inside the
 * current year it is noise on every divider, and across years its absence is a
 * genuine ambiguity.
 *
 * `now` is injectable so the "Hoy"/"Ayer" boundary is testable without freezing
 * the system clock. Callers pass nothing; the timeline re-renders on the 10s
 * message poll, so a midnight rollover corrects itself without a timer.
 */
export function dayDividerLabel(iso: string, now: Date = new Date()): string {
  const key = dayKey(iso);
  if (key === dayKey(now.toISOString())) return "Hoy";

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (key === dayKey(yesterday.toISOString())) return "Ayer";

  const d = new Date(iso);
  const format = d.getFullYear() === now.getFullYear() ? DAY_FORMAT : DAY_WITH_YEAR_FORMAT;
  return format.format(d);
}

export type DayGroup<T> = { key: string; label: string; items: T[] };

/**
 * Cut an already-ordered message list into consecutive days.
 *
 * Groups runs, not keys — the input arrives ordered by `seq` from the server and
 * that order is preserved as-is. Generic over `{ createdAt }` so both timelines
 * and the tests can use it without dragging in the whole `Message` type.
 *
 * Callers must pass the list they actually RENDER. Grouping before the channel
 * filter leaves a divider standing over a day whose messages were all hidden.
 */
export function groupByDay<T extends { createdAt: string }>(
  items: T[],
  now: Date = new Date(),
): DayGroup<T>[] {
  const groups: DayGroup<T>[] = [];
  for (const item of items) {
    const key = dayKey(item.createdAt);
    const current = groups[groups.length - 1];
    if (current && current.key === key) {
      current.items.push(item);
      continue;
    }
    groups.push({ key, label: dayDividerLabel(item.createdAt, now), items: [item] });
  }
  return groups;
}
